import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  MenuItem,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import { PetParentAnimal } from '../../../types/PetParent';
import { getBreedsByAnimalType } from '../../../utilities/breeds';

interface PetProfileEditProps {
  animal: PetParentAnimal;
  onSave: (updatedData: Partial<PetParentAnimal>) => void;
  onCancel: () => void;
  onFormChange?: () => void;
}

interface FormData {
  name: string;
  knowsBirthday: boolean;
  birthDate: Dayjs | null;
  ageYears: string;
  ageMonths: string;
  primaryBreed: string | null;
  gender: string;
  exitDate: Dayjs | null;
}

function PetProfileEdit({
 animal, onSave, onCancel, onFormChange,
}: PetProfileEditProps) {
  const calculateInitialAge = () => {
    if (!animal.birthDate) return { years: '', months: '' };
    const birth = moment(animal.birthDate);
    const now = moment();
    const years = now.diff(birth, 'years');
    const months = now.diff(birth, 'months') % 12;
    return { years: years.toString(), months: months.toString() };
  };

  const initialAge = calculateInitialAge();

  const [formData, setFormData] = React.useState<FormData>({
    name: animal.name || '',
    knowsBirthday: Boolean(animal.birthDate),
    birthDate: animal.birthDate ? dayjs(animal.birthDate) : null,
    ageYears: initialAge.years,
    ageMonths: initialAge.months,
    primaryBreed: animal.primaryBreed || null,
    gender: animal.gender || '',
    exitDate: animal.exitDate ? dayjs(animal.exitDate) : null,
  });

  const breeds = getBreedsByAnimalType(animal.animalType);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    onFormChange?.();
  };

  const handleSave = () => {
    let calculatedBirthDate = formData.birthDate;
    if (!formData.knowsBirthday && (formData.ageYears || formData.ageMonths)) {
      const years = parseInt(formData.ageYears || '0', 10);
      const months = parseInt(formData.ageMonths || '0', 10);
      calculatedBirthDate = dayjs().subtract(years, 'years').subtract(months, 'months');
    }

    const updatedData: Partial<PetParentAnimal> = {
      name: formData.name,
      birthDate: calculatedBirthDate ? calculatedBirthDate.format('M/D/YYYY') : animal.birthDate,
      primaryBreed: formData.primaryBreed || '',
      gender: formData.gender,
      exitDate: formData.exitDate ? formData.exitDate.format('M/D/YYYY') : animal.exitDate,
    };

    onSave(updatedData);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: '12px',
          boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{
          px: 2, py: 1.5, minHeight: '48px', display: 'flex', alignItems: 'center',
        }}
        >
          <Typography
            variant="h6"
            sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
          >
            Profile Editing
          </Typography>
        </Box>

        <Box sx={{
          px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 3,
        }}
        >
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            variant="outlined"
          />

          <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body1">How old is your pet?</Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={formData.knowsBirthday}
                  onChange={(e) => handleChange('knowsBirthday', e.target.checked)}
                  color="primary"
                />
              )}
              label="I know their exact birthday"
            />

            {formData.knowsBirthday ? (
              <Box>
                <DatePicker
                  label="Date of birth"
                  value={formData.birthDate}
                  onChange={(newValue) => handleChange('birthDate', newValue)}
                  maxDate={dayjs()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                    />
                  )}
                />
                <FormHelperText sx={{ pl: 2 }}>We&apos;ll calculate their age for you.</FormHelperText>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Years"
                    type="number"
                    value={formData.ageYears}
                    onChange={(e) => handleChange('ageYears', e.target.value)}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    label="Months"
                    type="number"
                    value={formData.ageMonths}
                    onChange={(e) => handleChange('ageMonths', e.target.value)}
                    variant="outlined"
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, max: 11 }}
                  />
                </Box>
                <FormHelperText sx={{ pl: 2 }}>If you’re not sure, an estimate is totally fine.</FormHelperText>
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

          <Autocomplete
            value={formData.primaryBreed}
            onChange={(event, newValue) => handleChange('primaryBreed', newValue)}
            options={breeds}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Primary breed"
                variant="outlined"
              />
            )}
            fullWidth
          />

          <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

          <TextField
            select
            fullWidth
            label="Sex"
            value={formData.gender}
            onChange={(e) => handleChange('gender', e.target.value)}
            variant="outlined"
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </TextField>

          <Divider sx={{ borderColor: 'rgba(238, 238, 238, 1)' }} />

          <DatePicker
            label="Adoption date"
            value={formData.exitDate}
            onChange={(newValue) => handleChange('exitDate', newValue)}
            maxDate={dayjs()}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
              />
            )}
          />
        </Box>

        <Box sx={{
          px: 2, py: 1.5, display: 'flex', gap: 1.5,
        }}
        >
          <Button
            onClick={onCancel}
            sx={{
              color: '#3700B3',
              width: '100%',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}

export default PetProfileEdit;
